import type {
  AdvertisingBudgetPaceRowDto,
  AdvertisingCostPerLeadRowDto,
} from "@/api/buyerReports";
import {
  SkeletonList,
  StatusBadge,
  SubTable,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { useCostPerLead } from "@/hooks/buyers/useBuyerReports";
import { monthLabel } from "../../indexTiles/tileBits";
import { QueryErrorState } from "../../queryStates";

function money(value: number): string {
  return formatCurrency(value);
}

/** A cost per X, null when the denominator was zero — an em dash, never $0. */
function cost(value?: number | null): string {
  return value == null ? "—" : formatCurrency(value);
}

const CPL_COLUMNS: SubTableColumn<AdvertisingCostPerLeadRowDto>[] = [
  { key: "ad", header: "Advertisement", render: (r) => r.advertisementName },
  { key: "channel", header: "Site / channel", render: (r) => r.channel },
  { key: "team", header: "Team", render: (r) => r.buyerTeamName },
  { key: "period", header: "Month", render: (r) => monthLabel(r.period) },
  { key: "spend", header: "Spend", align: "right", render: (r) => money(r.spend) },
  { key: "leads", header: "Leads", align: "right", render: (r) => r.leads },
  { key: "qualified", header: "Qualified", align: "right", render: (r) => r.qualified },
  { key: "cpl", header: "Cost per lead", align: "right", render: (r) => cost(r.costPerLead) },
  {
    key: "cpq",
    header: "Cost per qualified",
    align: "right",
    render: (r) => cost(r.costPerQualified),
  },
];

const BUDGET_COLUMNS: SubTableColumn<AdvertisingBudgetPaceRowDto>[] = [
  { key: "team", header: "Team", render: (r) => r.buyerTeamName },
  { key: "period", header: "Month", render: (r) => monthLabel(r.period) },
  {
    key: "budget",
    header: "Budget",
    align: "right",
    render: (r) => (r.budget == null ? "—" : money(r.budget)),
  },
  { key: "spend", header: "Spend", align: "right", render: (r) => money(r.spend) },
  {
    key: "pace",
    header: "Pace",
    // §6 — over budget is a state that needs attention, so it is a pill; the
    // absence of a problem is muted prose, and "no budget" is a missing
    // value, never a pill.
    render: (r) =>
      r.isOverBudget ? (
        <StatusBadge tone="danger">Over budget</StatusBadge>
      ) : r.budget == null ? (
        <span className="text-small text-gray-mid">No budget set</span>
      ) : (
        <span className="text-small text-gray-mid">Within budget</span>
      ),
  },
];

/**
 * Cost per lead by advertisement, site, team and month, plus budget pace —
 * both from the one report read, narrowed server-side to the teams the
 * caller may see.
 */
export function AdvertisingReportTab({
  from,
  to,
  teamId,
}: {
  from: string;
  to: string;
  teamId: string | undefined;
}) {
  const report = useCostPerLead({ from, to, buyerTeamId: teamId });

  if (report.isError) {
    return (
      <QueryErrorState
        what="the cost-per-lead report"
        onRetry={() => void report.refetch()}
        retrying={report.isFetching}
      />
    );
  }

  if (!report.data) return <SkeletonList rows={4} data-testid="advertising-loading" />;

  return (
    <div className="flex flex-col gap-space-6">
      <section aria-label="Cost per lead">
        <h2 className="font-display text-lead font-semibold text-onyx m-0 mb-space-3">
          Cost per lead
        </h2>
        <SubTable
          caption="Cost per lead by advertisement and month"
          columns={CPL_COLUMNS}
          rows={report.data.rows}
          rowKey={(r) => `${r.advertisementId}:${r.period}`}
          empty={{
            message: "Nothing spent and nobody captured in this period.",
            door: {
              kind: "derived",
              explain: "Rows appear once an advertisement records spend or captures a lead in the month.",
            },
          }}
        />
      </section>

      <section aria-label="Budget pace">
        <h2 className="font-display text-lead font-semibold text-onyx m-0 mb-space-3">
          Budget pace
        </h2>
        <SubTable
          caption="Budget against spend by team and month"
          columns={BUDGET_COLUMNS}
          rows={report.data.budgets}
          rowKey={(r) => `${r.buyerTeamId}:${r.period}`}
          empty={{
            message: "No teams in this period.",
            door: {
              kind: "derived",
              explain: "A row is emitted for every team you may see, for every month in the range.",
            },
          }}
        />
      </section>
    </div>
  );
}
